/* global insightsMetadataUrl insightsDataUrl */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';

import { downloadCsv } from './helpers/insightsExport';
import InsightsBuilder from './InsightsBuilder';
import InsightsChart from './InsightsChart';
import RatioCards from './RatioCards';
import SavedViews from './SavedViews';
import useInsightsView from './useInsightsView';

import useApiRequest from 'Hooks/useApiRequest';

import InfoIcon from 'Assets/icons/circle-info.svg';
import DownloadIcon from 'Assets/icons/download.svg';
import ExportIcon from 'Assets/icons/export.svg';

const DEFAULT_VIEW = {
    mode: 'breakdown',
    measure: 'budget_value',
    denominator: 'tickets_sold',
    dimension: 'department',
    split: null,
};

// Clicking a pre-built ratio card loads its matched grouped chart (Dave, comment
// 12): budget/seat & budget/ticket → by venue, budget/athlete → by discipline.
const CARD_VIEWS = {
    budget_per_athlete: { mode: 'ratio', measure: 'total_budget', denominator: 'athletes_per_sport', dimension: 'discipline', split: null },
    budget_per_seat: { mode: 'ratio', measure: 'total_budget', denominator: 'seats', dimension: 'venue', split: null },
    budget_per_ticket: { mode: 'ratio', measure: 'total_budget', denominator: 'tickets_sold', dimension: 'venue', split: null },
};

/**
 * Insights tab — self-service BI on the Budget dashboard (replaces Key Metrics).
 *
 * @ticket IB-644
 */
export default function Insights() {
    const [metadata, setMetadata] = useState(null);
    const [view, setView] = useState(DEFAULT_VIEW);
    const [chartType, setChartType] = useState('bar');
    const [activeViewId, setActiveViewId] = useState(null);
    // Same-axis filter: which of the grouped bars to show (empty = all). Applied
    // client-side so it's instant and always knows the full group list.
    const [filterKeys, setFilterKeys] = useState([]);
    const chartRef = useRef(null);

    const metadataApi = useApiRequest({ url: insightsMetadataUrl });
    const dataApi = useApiRequest({ url: insightsDataUrl, displayErrorToast: false });
    const { views: savedViews, saveView, deleteView } = useInsightsView(metadata ? metadata.savedViews : null);

    // Load metadata on mount.
    useEffect(() => {
        metadataApi
            .get()
            .then(({ data }) => setMetadata(data))
            .catch(() => {});
    }, []);

    const fetchData = useCallback(
        (nextView) => {
            const params = {
                mode: nextView.mode,
                measure: nextView.measure,
                dimension: nextView.dimension,
                chartType,
            };

            if (nextView.mode === 'ratio' && nextView.denominator) {
                params.denominator = nextView.denominator;
            }
            if (nextView.mode === 'breakdown' && nextView.split) {
                params.split = nextView.split;
            }
            if (
                nextView.mode === 'breakdown' &&
                nextView.measure === 'headcount' &&
                Array.isArray(nextView.headcountTypes) &&
                nextView.headcountTypes.length > 0
            ) {
                params.headcountTypes = nextView.headcountTypes;
            }

            dataApi.get(params).catch(() => {});
        },
        [chartType],
    );

    // Refetch whenever the builder view changes (chart-type is client-side only).
    useEffect(() => {
        if (metadata) {
            fetchData(view);
        }
    }, [metadata, view.mode, view.measure, view.denominator, view.dimension, view.split, view.headcountTypes]);

    const result = dataApi.data;
    const negativeCapable = result ? result.negativeCapable : false;

    // A donut cannot represent negative values (it would silently render
    // Math.abs and drop the sign), so fall back to a bar chart for a
    // negativeCapable measure such as `net`.
    useEffect(() => {
        if (negativeCapable && chartType === 'donut') {
            setChartType('bar');
        }
    }, [negativeCapable, chartType]);

    // The set of bars changes identity when the mode/measure/dimension changes,
    // so a stale filter selection must be cleared (denominator/split keep keys).
    useEffect(() => {
        setFilterKeys([]);
    }, [view.mode, view.measure, view.dimension]);

    // Full (unfiltered) groups drive the filter options; the filtered set drives
    // the chart, table, CSV and PNG.
    const availableGroups = (result && result.groups) || [];
    const displayResult = useMemo(() => {
        if (!result || filterKeys.length === 0) {
            return result;
        }
        return { ...result, groups: result.groups.filter((g) => filterKeys.includes(g.key)) };
    }, [result, filterKeys]);

    const handleSaveView = useCallback(
        (name) => {
            saveView(name, view, chartType)
                .then((created) => setActiveViewId(created.id))
                .catch(() => {});
        },
        [saveView, view, chartType],
    );

    // Manual builder edits drop the "applied saved view" highlight.
    const handleViewChange = useCallback((next) => {
        setView(next);
        setActiveViewId(null);
    }, []);

    const handleApplyView = useCallback((saved) => {
        setView({ ...DEFAULT_VIEW, ...saved.view });
        if (saved.chartType) {
            setChartType(saved.chartType);
        }
        setActiveViewId(saved.id);
    }, []);

    const handleDeleteView = useCallback(
        (id) => {
            deleteView(id).catch(() => {});
            setActiveViewId((current) => (current === id ? null : current));
        },
        [deleteView],
    );

    // Clicking a ratio card loads its matched grouped chart.
    const handleCardSelect = useCallback((cardKey) => {
        const cardView = CARD_VIEWS[cardKey];
        if (cardView) {
            setView({ ...DEFAULT_VIEW, ...cardView });
            setActiveViewId(null);
        }
    }, []);

    const excludedNote = useMemo(() => {
        if (!result || !result.excluded) {
            return null;
        }
        return $t('{{count}} group(s) left off — hover the empty column, or open table view, to see why.', {
            count: result.excluded,
        });
    }, [result]);

    if (!metadata) {
        return <div className="tw-py-12 tw-text-center tw-text-charcoal-50 tw-text-sm">{$t('Loading insights…')}</div>;
    }

    return (
        <div className="tw-font-sans tw-text-charcoal tw-pb-10">
            {view.mode === 'ratio' && <RatioCards cards={metadata.cards} onSelect={handleCardSelect} />}

            <InsightsBuilder
                metadata={metadata}
                view={view}
                onChange={handleViewChange}
                chartType={chartType}
                onChartType={setChartType}
                onSaveView={handleSaveView}
                negativeCapable={negativeCapable}
                availableGroups={availableGroups}
                filterKeys={filterKeys}
                onFilterChange={setFilterKeys}
            />

            <div className="tw-bg-white tw-border tw-border-charcoal-25 tw-rounded-xl tw-px-5 tw-py-4">
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-1.5">
                    <span className="tw-text-[15px] tw-font-medium">{result ? result.caption : ''}</span>
                    <div className="tw-flex tw-items-center tw-gap-3">
                        <button
                            type="button"
                            onClick={() => downloadCsv(displayResult)}
                            className="tw-text-xs tw-text-blue tw-inline-flex tw-items-center tw-gap-1"
                        >
                            <DownloadIcon className="tw-h-3.5 tw-w-3.5" /> {$t('CSV')}
                        </button>
                        <button
                            type="button"
                            onClick={() => chartRef.current && chartRef.current.exportPng()}
                            disabled={chartType === 'table'}
                            className="tw-text-xs tw-text-blue tw-inline-flex tw-items-center tw-gap-1 disabled:tw-text-charcoal-50 disabled:tw-cursor-not-allowed"
                        >
                            <ExportIcon className="tw-h-3.5 tw-w-3.5" /> {$t('PNG')}
                        </button>
                    </div>
                </div>

                {dataApi.loading ? (
                    <div className="tw-py-12 tw-text-center tw-text-charcoal-50 tw-text-sm">{$t('Loading…')}</div>
                ) : filterKeys.length > 0 && displayResult && displayResult.groups.length === 0 ? (
                    <div className="tw-py-12 tw-text-center tw-text-charcoal-50 tw-text-sm">{$t('No data matches the current filter.')}</div>
                ) : (
                    <InsightsChart ref={chartRef} result={displayResult} chartType={chartType} />
                )}

                {result && Array.isArray(result.flags) && result.flags.includes('venue_allocated_only') && (
                    <div className="tw-flex tw-gap-2.5 tw-items-start tw-bg-blue-25 tw-rounded-lg tw-px-3.5 tw-py-3 tw-mt-4 tw-text-[12.5px] tw-text-dark-blue">
                        <InfoIcon className="tw-text-blue tw-h-4 tw-w-4 tw-shrink-0 tw-mt-0.5" />
                        <span>
                            {$t('The venue ratio uses venue-allocated budget only. Budget with no venue tag is excluded so the bars do not overstate.')}
                        </span>
                    </div>
                )}

                {result && Array.isArray(result.flags) && result.flags.includes('discipline_allocated_only') && (
                    <div className="tw-flex tw-gap-2.5 tw-items-start tw-bg-blue-25 tw-rounded-lg tw-px-3.5 tw-py-3 tw-mt-4 tw-text-[12.5px] tw-text-dark-blue">
                        <InfoIcon className="tw-text-blue tw-h-4 tw-w-4 tw-shrink-0 tw-mt-0.5" />
                        <span>
                            {$t(
                                'The discipline ratio uses discipline-allocated budget only. Budget with no discipline tag is excluded so the bars do not overstate.',
                            )}
                        </span>
                    </div>
                )}

                {excludedNote && <div className="tw-text-[11px] tw-text-charcoal-50 tw-mt-3">{excludedNote}</div>}

                {result && result.truncated && (
                    <div className="tw-text-[11px] tw-text-charcoal-50 tw-mt-2">
                        {$t('Showing the top 20 groups; the remainder is aggregated into “Other”.')}
                    </div>
                )}
            </div>

            <SavedViews views={savedViews} activeId={activeViewId} onApply={handleApplyView} onDelete={handleDeleteView} />
        </div>
    );
}

if (document.getElementById('event-insights')) {
    const root = document.getElementById('event-insights');
    ReactDOM.render(
        <App>
            <Insights />
        </App>,
        root,
    );
}
